import { bool, func, array, object, string } from 'prop-types';
import React from 'react';

import CustomerDiscountForm from '../../components/OrderBreakdown/CustomerDiscountForm';
import { FeeIcon } from '../../components/Fee/FeeIcon';
import { formatMoney } from '../../util/currency';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  planAppliesTrial,
  subscriptionBreakdownMessageId,
  sumCustomerLineItemAmounts,
} from '../../util/digitalSubscription';
import {
  LINE_ITEM_CUSTOMER_COMMISSION,
  discountCodeFromLineItemCode,
  isLineItemCustomerDiscount,
} from '../../util/types';

import breakdownCss from '../../components/OrderBreakdown/OrderBreakdown.module.css';
import css from './CheckoutPage.module.css';

const { Money } = sdkTypes;

const percentageLabel = percentage => {
  if (percentage == null) {
    return null;
  }
  if (typeof percentage.abs === 'function') {
    return percentage.abs().toString();
  }
  return String(Math.abs(percentage));
};

const SubscriptionCheckoutBreakdown = props => {
  const {
    listing,
    plan,
    lineItems,
    intl,
    marketplaceName,
    validateDiscountInProgress,
    validateDiscountError,
    onValidateDiscount,
  } = props;

  if (!plan) {
    return null;
  }

  const hasTrial = planAppliesTrial(listing, plan);
  const planMoney = new Money(plan.amount, plan.currency);
  const planPrice = formatMoney(intl, planMoney);
  const customerItems = (lineItems || []).filter(
    item => item?.includeFor?.includes('customer') && !item.reversal
  );
  const discountLine = customerItems.find(item => isLineItemCustomerDiscount(item.code));
  const feeLine = customerItems.find(item => item.code === LINE_ITEM_CUSTOMER_COMMISSION);
  const payin = sumCustomerLineItemAmounts(lineItems) || {
    amount: plan.amount,
    currency: plan.currency,
  };
  const payinMoney = new Money(payin.amount, payin.currency);
  const handleDiscountSubmit = userTryOnDiscountCode => {
    if (onValidateDiscount && listing?.id) {
      onValidateDiscount(listing.id, userTryOnDiscountCode, { skipReload: true });
    }
  };

  return (
    <div className={css.subscriptionBreakdown} data-testid="subscription-listing-breakdown">
      <div className={css.subscriptionBreakdownRow} data-testid="subscription-breakdown-subtotal">
        <span>
          <FormattedMessage id={subscriptionBreakdownMessageId(plan.interval)} />
        </span>
        <span>{planPrice}</span>
      </div>
      {discountLine ? (
        <div className={css.subscriptionBreakdownRow} data-testid="subscription-breakdown-discount">
          <span>
            <FormattedMessage
              id="OrderBreakdown.customerDiscount"
              values={{
                discountCode: discountCodeFromLineItemCode(discountLine.code),
                isCapped: !!discountLine.isCapped,
                percentage: percentageLabel(discountLine.percentage),
              }}
            />
          </span>
          <span>{formatMoney(intl, discountLine.lineTotal)}</span>
        </div>
      ) : null}
      {feeLine ? (
        <div className={css.subscriptionBreakdownRow} data-testid="subscription-breakdown-fee">
          <span className={breakdownCss.itemLabelWithIcon}>
            <FormattedMessage
              id="OrderBreakdown.commission"
              values={{ marketplaceName, role: 'customer' }}
            />
            <FeeIcon
              type="transaction"
              commissionPercentage={percentageLabel(feeLine.percentage)}
            />
          </span>
          <span>{formatMoney(intl, feeLine.lineTotal)}</span>
        </div>
      ) : null}
      <div className={css.subscriptionBreakdownTotal} data-testid="subscription-breakdown-total">
        <span>
          <FormattedMessage
            id={hasTrial ? 'CheckoutPage.afterYourTrial' : 'CheckoutPage.breakdownTotal'}
          />
        </span>
        <span>{formatMoney(intl, payinMoney)}</span>
      </div>
      <div className={css.subscriptionDiscountForm} data-testid="subscription-discount-form">
        <CustomerDiscountForm
          validateDiscountError={validateDiscountError}
          onSubmit={handleDiscountSubmit}
          inProgress={validateDiscountInProgress}
        />
      </div>
    </div>
  );
};

SubscriptionCheckoutBreakdown.defaultProps = {
  lineItems: null,
  marketplaceName: 'Soulwise',
  validateDiscountInProgress: false,
  validateDiscountError: null,
  onValidateDiscount: null,
};

SubscriptionCheckoutBreakdown.propTypes = {
  listing: object.isRequired,
  plan: object.isRequired,
  lineItems: array,
  intl: intlShape.isRequired,
  marketplaceName: string,
  validateDiscountInProgress: bool,
  validateDiscountError: string,
  onValidateDiscount: func,
};

export default SubscriptionCheckoutBreakdown;
